import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  FraudTokensQuery,
  CreateFraudTokenMutation,
  CreateFraudTokenMutationVariables,
  PreviewFraudTokenQuery,
  PreviewFraudTokenQueryVariables,
} from '@pv/common/graphql/operations';
import { FraudTokenTypeEnum } from '@pv/common/graphql/types';
import {
  CREATE_FRAUD_TOKEN_MUTATION,
  PREVIEW_FRAUD_TOKEN_QUERY,
} from './graphql';
import CreateFraudTokenForm from './CreateFraudTokenForm';

interface AddFraudTokenDialogProps {
  open: boolean;
  onClose: () => void;
  token?: FraudTokensQuery['fraudTokens'][number] | null;
}
interface FormData {
  tokenType: FraudTokenTypeEnum;
  value: string;
  notes: string;
}
export const AddFraudTokenDialog = ({
  open,
  onClose,
  token,
}: AddFraudTokenDialogProps) => {
  const [formData, setFormData] = useState<FormData>({
    tokenType: FraudTokenTypeEnum.Email,
    value: '',
    notes: '',
  });

  useEffect(() => {
    setFormData({
      tokenType: FraudTokenTypeEnum.Email,
      value: '',
      notes: '',
    });
  }, []);

  const [createFraudToken] = useMutation<
    CreateFraudTokenMutation,
    CreateFraudTokenMutationVariables
  >(CREATE_FRAUD_TOKEN_MUTATION);

  const [previewFraudToken, { data: previewData, loading: previewLoading }] =
    useLazyQuery<PreviewFraudTokenQuery, PreviewFraudTokenQueryVariables>(
      PREVIEW_FRAUD_TOKEN_QUERY
    );

  const handlePreview = () => {
    if (formData.value && formData.tokenType) {
      previewFraudToken({
        variables: {
          tokenType: formData.tokenType,
          value: formData.value,
        },
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await createFraudToken({
        variables: {
          tokenType: formData.tokenType,
          value: formData.value,
          notes: formData.notes || null,
        },
      });

      onClose();
    } catch (error) {
      console.error('Error saving fraud token:', error);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add To Fraud Scanner</DialogTitle>
      <DialogContent>
        <CreateFraudTokenForm
          tokenType={formData.tokenType}
          value={formData.value}
          notes={formData.notes}
          onTokenTypeChange={(value) =>
            setFormData({ ...formData, tokenType: value as FraudTokenTypeEnum })
          }
          onValueChange={(value) => setFormData({ ...formData, value })}
          onNotesChange={(value) => setFormData({ ...formData, notes: value })}
        />

        <Box sx={{ mt: 2 }}>
          <Button
            onClick={handlePreview}
            variant="outlined"
            disabled={!formData.value || previewLoading}
            fullWidth
          >
            {previewLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading Preview...
              </>
            ) : (
              'Preview Retroactive Stats'
            )}
          </Button>
        </Box>

        {previewData?.previewFraudToken && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview Results
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={`${previewData.previewFraudToken.totalEvents} Total Events`}
                color="default"
              />
              <Chip
                label={`${previewData.previewFraudToken.confirmedFraudCount} Confirmed Fraud`}
                color="error"
              />
              <Chip
                label={`${previewData.previewFraudToken.falsePositiveCount} False Positives`}
                color="warning"
              />
              <Chip
                label={`${previewData.previewFraudToken.unknownCount} Unknown`}
                color="default"
              />
            </Box>
            {previewData.previewFraudToken.precision !== undefined &&
              previewData.previewFraudToken.precision !== null && (
                <Alert
                  severity={
                    previewData.previewFraudToken.precision >= 80
                      ? 'success'
                      : previewData.previewFraudToken.precision >= 50
                      ? 'warning'
                      : 'error'
                  }
                >
                  <Typography variant="body2">
                    <strong>
                      Precision: {previewData.previewFraudToken.precision}%
                    </strong>
                    {previewData.previewFraudToken.precision >= 80 && (
                      <> - Excellent signal for fraud detection</>
                    )}
                    {previewData.previewFraudToken.precision >= 50 &&
                      previewData.previewFraudToken.precision < 80 && (
                        <> - Moderate signal, may have some false positives</>
                      )}
                    {previewData.previewFraudToken.precision < 50 && (
                      <> - Low precision, may not be a reliable indicator</>
                    )}
                  </Typography>
                </Alert>
              )}
            {previewData.previewFraudToken.totalEvents === 0 && (
              <Alert severity="info">
                No historical events match this token. It will only detect
                future fraud.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.value}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
